import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { persistor } from "../../redux/store";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "../../context/ThemeContext";
import { logoutUser } from "../../redux/ApiSlice/LogoutSlice";
import { getPrivateHas } from "../../redux/ApiSlice/AccountPrivateSlice";
import { useCallback, useEffect, useState } from "react";
import { setClearHomepageVideos } from "../../redux/ApiSlice/HomePageVideoSlice";
import { getPublicPrivateToggle, postPublicPrivateToggle } from "../../redux/ApiSlice/UserStatusSlice";
import { CircleUserRound, UserRoundCheck, LockKeyhole, Shield, FileKey, SquareActivity, LogOut, Wrench, MessageCircleQuestionMark, ChevronRight, Flag, Sparkles, ShieldCheck } from "lucide-react";
import { clearAccountProfile } from "../../redux/ApiSlice/AccountProfileSlice";
import { logout } from "../../redux/ApiSlice/Auth/AuthSlice";
import { clearStorageKeepingPreferences } from "../../utils/userPreferences";

const AccountSettings = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();
    const [isDarkMode, setIsDarkMode] = useState(theme === "dark");
    const { hasPin } = useSelector((state) => state.AccountPrivate);
    const [toggleChecked, setToggleChecked] = useState({ is_private: false });

    const User_Status = useSelector((state) => state.UserPrivate_Status);
    const { User_PrivateStatus } = User_Status;
    const { user_data } = User_PrivateStatus;

    const keepLogin = localStorage.getItem("keepMeLogin");

    const handleLogOut = async () => {
        try {
            await dispatch(logoutUser());
            await dispatch(logout());
            await persistor.flush();
            await persistor.purge();
            await dispatch(clearAccountProfile());
            await dispatch(setClearHomepageVideos());
            localStorage.removeItem('accessToken');
            if (keepLogin) {
                localStorage.removeItem("security_login");
                localStorage.removeItem("persist:root");
            } else {
                clearStorageKeepingPreferences();
            }
            navigate("/login");
        } catch (error) {
            console.error("Logout error:", error);
            clearStorageKeepingPreferences();
            navigate("/login");
        }
    };

    const handleChangeToggle = async (isChecked) => {
        setToggleChecked({ is_private: !isChecked });
        try {
            await dispatch(postPublicPrivateToggle({ is_private: !isChecked })).unwrap();
            toast.success(!isChecked ? "Your account is now private" : "Your account is now public");
        } catch (error) {
            setToggleChecked({ is_private: isChecked });
            toast.error("Failed to update account privacy");
        }
    };

    const handleThemeToggle = () => {
        setIsDarkMode(!isDarkMode);
        toggleTheme();
    };

    const handleSelected = useCallback(() => {
        setToggleChecked({ is_private: user_data?.is_private });
    }, [user_data])

    useEffect(() => {
        handleSelected();
    }, [handleSelected]);

    useEffect(() => {
        dispatch(getPrivateHas());
        dispatch(getPublicPrivateToggle());

    }, [dispatch]);

    useEffect(() => {
        setIsDarkMode(theme === "dark");
    }, [theme]);

    const settingsList = [
        { name: "Account Settings", icon: <CircleUserRound size={16} />, path: "/settings/account", type: "navigate" },
        { name: "Private Account", icon: <LockKeyhole size={16} />, path: null, type: "toggle" },
        { name: "My Interests", icon: <Sparkles size={16} />, path: "/settings/interests", type: "navigate" },
        // { name: "Dark Mode", icon: <Sun size={16} />, path: null, type: "toggle" },
        { name: "Private", icon: <Shield size={16} />, path: hasPin.has_pin ? "/private" : "/private/empty", type: "navigate" },
        { name: "Your Activity", icon: <SquareActivity size={16} />, path: "/settings/youractivity", type: "navigate" },
        { name: "Privacy, Safety & Policies", icon: <FileKey size={16} />, path: "/settings/privacy-safety", type: "navigate" },
        { name: "Friendship Suggestions", icon: <UserRoundCheck size={16} />, path: "/user/suggestion/friendship", type: "navigate" },
        { name: "Parental Controls", icon: <ShieldCheck size={16} />, path: "/settings/parental-controls", type: "navigate" },
        { name: "Report Center", icon: <Flag size={16} />, path: "/report-center", type: "navigate" },
        // { name: "Content Based Friends", icon: <UserRoundPlus size={16} />, path: "/user/suggestion/content-based", type: "navigate" },
        { name: "Preferences", icon: <Wrench size={16} />, path: "/settings/preferences", type: "navigate" },
        { name: "Help", icon: <MessageCircleQuestionMark size={16} />, path: "/help", type: "navigate" },
        // { name: "Download My Videos", icon: <HardDriveDownload size={16} />, path: "/settings/download-videos", type: "navigate" },
        { name: "Log Out", icon: <LogOut size={16} />, path: null, type: "logout" },
    ];

    return (
        <div className="h-full bg-white dark:bg-[#1E1D21] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 z-10">
                <div className="relative bg-[#BDE6FF] rounded-b-xl h-12 flex items-center justify-center">
                    <IoMdArrowBack
                        onClick={() => navigate("/account")}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[22px] text-black dark:text-black cursor-pointer"
                    />
                    <p className="font-bold text-[16px] text-black dark:text-black">Settings & Privacy</p>
                </div>
            </div>

            {/* Settings List */}
            {settingsList.map((item, index) => (
                <motion.div
                    key={index}
                    onClick={() => {
                        if (item.type === "navigate" && item.path) {
                            navigate(item.path);
                        } else if (item.type === "logout") {
                            handleLogOut();
                        }
                    }}
                    className="flex items-center justify-between px-3 py-3 border-b border-gray-300/50 dark:border-gray-800/50 text-black dark:text-white cursor-pointer"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="flex items-center space-x-2">
                        <span>{item.icon}</span>
                        <p className="font-medium text-[14px]">{item.name}</p>
                    </div>
                    {item.type === "toggle" ? (
                        item.name === "Dark Mode" ? (
                            <div
                                className="autoSaverSwitch relative inline-flex cursor-pointer select-none items-center"
                                onClick={(e) => { e.stopPropagation(); handleThemeToggle(); }}
                            >
                                <span className={`slider flex h-[19px] w-[40px] items-center rounded-full p-1 duration-200 ${isDarkMode ? "bg-[#BDE6FF]" : "bg-[#d9d9d9]"}`}>
                                    <span className={`dot h-[13px] w-[13px] rounded-full bg-[#666666] shadow-2xl duration-200 ${isDarkMode ? "translate-x-5" : ""}`} />
                                </span>
                            </div>
                        ) : (
                            <div
                                className="autoSaverSwitch relative inline-flex cursor-pointer select-none items-center"
                                onClick={(e) => { e.stopPropagation(); handleChangeToggle(toggleChecked.is_private); }}
                            >
                                <span className={`slider flex h-[19px] w-[40px] items-center rounded-full p-1 duration-200 ${toggleChecked.is_private ? "bg-[#BDE6FF]" : "bg-[#d9d9d9]"}`}>
                                    <span className={`dot h-[13px] w-[13px] rounded-full bg-[#666666] shadow-2xl duration-200 ${toggleChecked.is_private ? "translate-x-5" : ""}`} />
                                </span>
                            </div>
                        )
                    ) : (
                        <motion.div
                            whileHover={{ x: 3, scale: 1.1 }}
                            transition={{
                                type: "spring",
                                stiffness: 400,
                                damping: 10
                            }}
                        >
                            <ChevronRight size={16} className="text-gray-400 dark:text-gray-500" />
                        </motion.div>
                    )}
                </motion.div>
            ))}
        </div>
    )
}

export default AccountSettings